import { useFlag } from "@unleash/proxy-client-react";
import { differenceInDays, isValid, parseISO } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  cancelSubscription,
  cancelSubscriptionAddon,
  getNextCharge,
  reactivateSubscriptionAddon,
} from '../../../../../helpers/subscription';
import { formatDate } from '../../../../../helpers/formatDate';
import { OfferAPI } from "../../../../../api/payments/offer/api";
import { getClientAccessStatus } from "../../../../../api";
import { ClientsAPI } from "../../../../../api/payments/clients/api";
import { UserAccessControlAPI } from "../../../../../api/payments/userAccessControl/api";
import { ChargesAPI } from "../../../../../api/jusbill/charges/api";
import {
  CourtesyBlockedError,
  useFinanceiroCourtesySubmit,
} from "../../financeiro.courtesy.hook";

const canceledStatus = ["canceled", "CANCELED"];

export function useFinancialBootstrap({ props }) {
  const dispatch = useDispatch();
  const isFeatureFlagEnabled = useFlag('EnablePixPayments');
  const enableOrganicMigration = useFlag('enableOrganicMigrationAdmin');
  const [isSettlingCharge, setIsSettlingCharge] = useState(false);
  const [isLoadingPaymentLink, setIsLoadingPaymentLink] = useState(false);
  const [isCancelingModal, setIsCancelingModal] = useState(false);
  const [isPixModal, setIsPixModal] = useState(false);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [paymentLinkModal, setPaymentLinkModal] = useState({});
  const [isCourtesyModalOpen, setIsCourtesyModalOpen] = useState(false);
  const [isCourtesyLoading, setIsCourtesyLoading] = useState(false);
  const isUserAccessFlagEnabled = useFlag('USER_ACCESS_CONTROL');
  const [clientAccessStatus, setClientAccessStatus] = useState(null);
  const [hasFetchedClientAccessStatus, setHasFetchedClientAccessStatus] = useState(false);
  const [isBlockingClientAccess, setIsBlockingClientAccess] = useState(false);
  const [isBlockAccessModalOpen, setIsBlockAccessModalOpen] = useState(false);
  const [isUnblockAccessModalOpen, setIsUnblockAccessModalOpen] = useState(false);
  const [blockAccessExpiresAt, setBlockAccessExpiresAt] = useState('');
  const [isIndefiniteBlockAccess, setIsIndefiniteBlockAccess] = useState(false);
  const [removeItemModal, setRemoveItemModal] = useState({ isOpen: false, type: null, action: null, item: null });
  const [isRemovingItem, setIsRemovingItem] = useState(false);
  const [nextCharge, setNextCharge] = useState(null);
  const [nextChargeRefreshKey, setNextChargeRefreshKey] = useState(0);
  const accessStatusRequestRef = useRef(0);
  const nextChargeRequestRef = useRef(0);
  const customer = useSelector((state) => state.subscription.customer);

  const user = useSelector((state) => state.auth.user);
  const clientSubscription = useSelector(
    (state) => state.app.client_subscription,
  );
  const isLoadingClientSubscription = useSelector(
    (state) => state.app.is_loading_client_subscription,
  );
  const isDeleted = props.formik.values.deleted_at;
  const settleCharge = async () => {
    if (
      window.confirm(
        'Confirma pagamento via PIX? Ação não válida para pagamento via boleto.',
      )
    ) {
      setIsSettlingCharge(true);
      try {
        const response = await ClientsAPI.settleCharge(props.cliente_id);
        if (response && response.data.success) {
          toast.success('Pagamento baixado com sucesso!');
          dispatch({
            type: 'LOAD_CLIENT_SUBSCRIPTION',
            payload: {
              id: props.cliente_id,
              useV2: !!clientSubscription?.external_id,
            },
          });
        } else {
          toast.error('Ocorreu um erro ao dar baixa no pagamento.');
        }
      } catch (err) {
        toast.error('Ocorreu um erro ao dar baixa no pagamento.');
      }
      setIsSettlingCharge(false);
    }
  };

  const refreshSubscription = () => {
    dispatch({
      type: 'LOAD_CLIENT_SUBSCRIPTION',
      payload: { id: props.cliente_id, useV2: true },
    });
    setNextChargeRefreshKey((key) => key + 1);
  };

  const handleCancel = async (reason) => {
    try {
      await cancelSubscription(props.cliente_id, user.client_id, reason);
      toast.success('A assinatura foi cancelada com sucesso.');
      dispatch({
        type: 'LOAD_CLIENT_SUBSCRIPTION',
        payload: { id: props.cliente_id, useV2: true },
      });
      setIsCancelingModal(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          'Ocorreu um erro ao cancelar a assinatura, por favor, tente novamente mais tarde.',
      );
    }
  };

  const cancelClick = () => {
    setIsCancelingModal(true);
  };

  const fetchClientAccessStatus = useCallback(async () => {
    const requestId = ++accessStatusRequestRef.current;
    if (!props.cliente_id) return;
    try {
      const response = await getClientAccessStatus(props.cliente_id);
      if (accessStatusRequestRef.current !== requestId) return;
      setClientAccessStatus(response.data?.data ?? null);
    } catch (error) {
      if (accessStatusRequestRef.current !== requestId) return;
      setClientAccessStatus(null);
    } finally {
      if (accessStatusRequestRef.current === requestId) {
        setHasFetchedClientAccessStatus(true);
      }
    }
  }, [props.cliente_id]);

  const handleToggleClientAccess = () => {
    if (clientAccessStatus?.isBlocked) {
      setIsUnblockAccessModalOpen(true);
    } else {
      setIsBlockAccessModalOpen(true);
    }
  };

  const blockClientAccess = async () => {
    if (!props.cliente_id) {
      toast.error('Cliente ainda não carregado, aguarde e tente novamente.');
      return;
    }
    if (!isIndefiniteBlockAccess && !blockAccessExpiresAt) {
      toast.error('Informe a data de expiração do bloqueio ou marque bloqueio indefinido.');
      return;
    }
    setIsBlockingClientAccess(true);
    const payload = {
      clientId: props.cliente_id,
      expires_at: isIndefiniteBlockAccess
        ? new Date('3000-01-01T23:59:59').toISOString()
        : new Date(blockAccessExpiresAt + 'T23:59:59').toISOString(),
      reason: 'Bloqueio do acesso a plataforma pelo Admin.',
    };
    try {
      const response = await UserAccessControlAPI.block(payload);
      if (response.status < 200 || response.status >= 300) {
        toast.error(response.data?.message ?? 'Erro ao bloquear usuário.');
      } else {
        toast.success(response.data?.message ?? 'Usuário bloqueado com sucesso.');
        await fetchClientAccessStatus();
        dispatch({
          type: 'LOAD_CLIENT_SUBSCRIPTION',
          payload: {
            id: payload.clientId,
            useV2: !!clientSubscription?.external_id,
          },
        });
      }
    } catch (err) {
      const details = err.response?.data?.details;
      const detailMessage = details ? Object.values(details).join(' ') : null;
      toast.error(
        err.response?.data?.message ||
          detailMessage ||
          'Ocorreu um erro ao bloquear o usuário, por favor, tente novamente.',
      );
    } finally {
      setIsBlockingClientAccess(false);
      setIsBlockAccessModalOpen(false);
      setBlockAccessExpiresAt('');
      setIsIndefiniteBlockAccess(false);
    }
  };

  const unblockClientAccess = async () => {
    if (!props.cliente_id) {
      toast.error('Cliente ainda não carregado, aguarde e tente novamente.');
      return;
    }
    setIsBlockingClientAccess(true);
    try {
      const response = await UserAccessControlAPI.unblock(props.cliente_id);
      if (response.status < 200 || response.status >= 300) {
        toast.error(response.data?.message ?? 'Erro ao desbloquear usuário.');
      } else {
        toast.success(response.data?.message ?? 'Usuário desbloqueado com sucesso.');
        await fetchClientAccessStatus();
        dispatch({
          type: 'LOAD_CLIENT_SUBSCRIPTION',
          payload: {
            id: props.cliente_id,
            useV2: !!clientSubscription?.external_id,
          },
        });
      }
    } catch (err) {
      const details = err.response?.data?.details;
      const detailMessage = details ? Object.values(details).join(' ') : null;
      toast.error(
        err.response?.data?.message ||
          detailMessage ||
          'Ocorreu um erro ao desbloquear o usuário, por favor, tente novamente.',
      );
    } finally {
      setIsBlockingClientAccess(false);
      setIsUnblockAccessModalOpen(false);
    }
  };

  const openRemoveItemModal = (type, item, action = 'remove') => {
    setRemoveItemModal({ isOpen: true, type, action, item });
  };

  const closeRemoveItemModal = () => {
    setRemoveItemModal({ isOpen: false, type: null, action: null, item: null });
  };

  const confirmRemoveItem = async () => {
    const { type, action, item } = removeItemModal;

    if (type !== 'addon') {
      closeRemoveItemModal();
      return;
    }

    const isReactivation = action === 'reactivate';
    const request = isReactivation ? reactivateSubscriptionAddon : cancelSubscriptionAddon;

    setIsRemovingItem(true);
    try {
      await request(clientSubscription?.info?.subscription_id, item.id);
      toast.success(
        isReactivation
          ? 'Add-on reativado. Ele volta a ser cobrado na próxima renovação.'
          : `Remoção agendada. O add-on fica ativo até ${formatDate(clientSubscription?.info?.current_period_end)}.`,
      );
      refreshSubscription();
      closeRemoveItemModal();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          (isReactivation
            ? 'Ocorreu um erro ao reativar o add-on, por favor, tente novamente.'
            : 'Ocorreu um erro ao remover o add-on, por favor, tente novamente.'),
      );
    } finally {
      setIsRemovingItem(false);
    }
  };

  const handlePix = async () => {
    if (!isFeatureFlagEnabled) {
      return;
    }
    if (
      !clientSubscription?.client_id ||
      !clientSubscription?.plan_details?.gateway_id
    ) {
      toast.error(
        'Dados da assinatura incompletos. Por favor, recarregue a página.',
      );
      return;
    }
    try {
      setPixLoading(true);
      const response = await ChargesAPI.createPix(
        clientSubscription.client_id,
        clientSubscription.plan_details.gateway_id,
      );
      if (![200, 201, 204].includes(response.status)) {
        throw new Error('Erro ao gerar o pix.');
      }
      const lastTx =
        response?.data?.charges?.[0]?.last_transaction ||
        response?.data?.last_transaction;
      if (!lastTx?.expires_at) {
        throw new Error('Resposta inválida ao gerar PIX (dados ausentes).');
      }
      const timer = Math.floor(
        (new Date(lastTx.expires_at) - new Date()) / 1000,
      );
      setPixData({ ...response.data, timer });
      setIsPixModal(true);
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          'Ocorreu um erro ao gerar o pix, por favor, tente novamente mais tarde.',
      );
    } finally {
      setPixLoading(false);
    }
  };

  const handleCreateCourtesy = async (data) => {
    if (!props.cliente_id) {
      toast.error('Cliente não encontrado.');
      return;
    }
    setIsCourtesyLoading(true);
    try {
      const response = await createCourtesy(data);

      if (![200, 201, 204].includes(response.status)) {
        throw new Error('Erro ao criar cortesia.');
      }

      toast.success('Cortesia criada com sucesso!');
      setIsCourtesyModalOpen(false);
      dispatch({
        type: 'LOAD_CLIENT_SUBSCRIPTION',
        payload: { id: props.cliente_id },
      });
    } catch (error) {
      toast.error(
        error instanceof CourtesyBlockedError
          ? error.message
          : error.response?.data?.message ||
              'Ocorreu um erro ao criar a cortesia, por favor, tente novamente.',
      );
    } finally {
      setIsCourtesyLoading(false);
    }
  };

  const isPeriodEndingSoon = () => {
    const allowedStatuses = [
      'unpaid',
      'waiting_payment',
      'pending_payment',
      'pending',
      'failed',
      'canceled',
    ];
    if (allowedStatuses.includes(clientSubscription.info.status)) return true;
    if (!clientSubscription.info?.current_period_end) return false;
    const endDate = parseISO(clientSubscription.info.current_period_end);
    if (!isValid(endDate)) return false;
    return differenceInDays(endDate, new Date()) <= 2;
  };

  const handleCheckPaymentLink = async (originFlow) => {
    try {
      setIsLoadingPaymentLink(true);

      const data = await OfferAPI.getActiveV2(clientSubscription?.client_id);

      if (!data.success) {
        setPaymentLinkModal({ isOpen: true, originFlow });
      } else {
        setPaymentLinkModal({
          isOpen: true,
          originFlow,
          data: { ...data, email: customer?.email },
        });
      }
    } catch (error) {
      toast.error('Ocorreu um erro ao verificar o link de pagamento.');
    } finally {
      setIsLoadingPaymentLink(false);
    }
  };

  const handleUpdatePlanOrActivateButton = async () => {
    const flow = canceledStatus.includes(clientSubscription.info.status)
      ? 'reactivation_plan'
      : 'upgrade_plan';
    if (flow === 'reactivation_plan' && clientSubscription.external_id) {
      // call reativation route
      toast.warn('reactivation will be implemented for jussub ASP...');
      return;
    }
    await handleCheckPaymentLink(flow);
  };

  const isJussubClient = useMemo(() => !!customer?.external_id, [
    customer?.external_id,
  ]);

  const hasCustomerLoaded = Object.keys(customer).length > 0;
  const isCustomerResolved =
    hasCustomerLoaded && customer?.id === props.cliente_id;

  const {
    createCourtesy,
    useJusSub: useJusSubCourtesy,
  } = useFinanceiroCourtesySubmit(props.cliente_id, {
    isJussubClient,
    isCustomerLoaded: isCustomerResolved,
  });

  const isCourtesyReady =
    isCustomerResolved && (isJussubClient || !isLoadingClientSubscription);

  useEffect(() => {
    if (customer?.id === props.cliente_id) return;
    dispatch({
      type: 'LOAD_SUBSCRIPTION_CUSTOMER',
      payload: { id: props.cliente_id },
    });
  }, [dispatch, props.cliente_id, customer?.id]);

  useEffect(() => {
    if (!isUserAccessFlagEnabled) return;
    fetchClientAccessStatus();
  }, [isUserAccessFlagEnabled, fetchClientAccessStatus]);

  useEffect(() => {
    if (!hasCustomerLoaded) return;
    dispatch({
      type: 'LOAD_CLIENT_SUBSCRIPTION',
      payload: { id: props.cliente_id, useV2: true },
    });

    dispatch({
      type: 'LOAD_CLIENT_PLAN_OFFERS',
      payload: { client_id: props.cliente_id },
    });

    return () => {
      dispatch({ type: 'CLEAR_CLIENT_PLAN_OFFERS' });
    };
  }, [props.cliente_id, hasCustomerLoaded, dispatch]);

  // Valor autoritativo da próxima cobrança vindo do JusSub (considera cupons
  // expirando, addons e proração). Exclusivo do JusSub — só buscamos quando o
  // provider é jussub e há subscription_id; para Jusbill (ou carregando/erro)
  // a tela mantém o valor derivado em clientSubscription.info.
  const subscriptionId = clientSubscription?.info?.subscription_id;
  const isJussubSubscription = clientSubscription?.info?.provider === 'jussub';

  useEffect(() => {
    if (!isJussubSubscription || !subscriptionId) {
      setNextCharge(null);
      return;
    }
    const requestId = ++nextChargeRequestRef.current;
    getNextCharge(subscriptionId, props.cliente_id)
      .then((data) => {
        if (nextChargeRequestRef.current !== requestId) return;
        setNextCharge(data ?? null);
      })
      .catch(() => {
        if (nextChargeRequestRef.current !== requestId) return;
        setNextCharge(null);
      });
  }, [isJussubSubscription, subscriptionId, props.cliente_id, nextChargeRefreshKey]);

  const canCancelPlan =
    Object.keys(clientSubscription ?? {}).length > 0 &&
    !!clientSubscription?.info?.status &&
    !canceledStatus.includes(clientSubscription.info.status) &&
    !isDeleted;

  return {
    isLoadingClientSubscription,
    isSettlingCharge,
    isLoadingPaymentLink,
    clientSubscription,
    customer,
    isJussubClient,
    nextCharge,
    paymentLinkModal,
    setPaymentLinkModal,
    handleUpdatePlanOrActivateButton,

    // Pagar com Pix
    isPixPaymentEnabled: isFeatureFlagEnabled,
    handlePix,
    pixLoading,
    isPixModal,
    setIsPixModal,
    pixData,

    // Cancelar plano
    canCancelPlan,
    isCancelingModal,
    setIsCancelingModal,
    cancelClick,
    handleCancel,

    // Cortesia,
    setIsCourtesyModalOpen,
    isCourtesyModalOpen,
    handleCreateCourtesy,
    isCourtesyLoading,
    useJusSubCourtesy,
    isCourtesyReady,

    // Bloquear/desbloquear acesso
    isClientAccessControlEnabled: isUserAccessFlagEnabled,
    isClientAccessBlocked: !!clientAccessStatus?.isBlocked,
    hasFetchedClientAccessStatus,
    isBlockingClientAccess,
    isBlockAccessModalOpen,
    setIsBlockAccessModalOpen,
    isUnblockAccessModalOpen,
    setIsUnblockAccessModalOpen,
    blockAccessExpiresAt,
    setBlockAccessExpiresAt,
    isIndefiniteBlockAccess,
    setIsIndefiniteBlockAccess,
    handleToggleClientAccess,
    blockClientAccess,
    unblockClientAccess,

    // Remover addon/cupom
    removeItemModal,
    isRemovingItem,
    openRemoveItemModal,
    closeRemoveItemModal,
    confirmRemoveItem,
  };
}
